import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Table, Alert, Row, Col, Badge } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { AlertCircle, CheckCircle2, FileSliders, UploadCloud } from 'lucide-react';

const DIAMOND_FIELDS = [
  { key: 'source', label: 'Source (Lab grown/natural)' },
  { key: 'cut', label: 'Shape' },
  { key: 'carat', label: 'Carat' },
  { key: 'color', label: 'Color' },
  { key: 'fancyColor', label: 'Fancy Color' },
  { key: 'fancy_color_inte', label: 'Fancy Color Intensity' },
  { key: 'clarity', label: 'Clarity' },
  { key: 'fluorescence', label: 'Fluorescence' },
  { key: 'symmetry', label: 'Symmetry' },
  { key: 'polish', label: 'Polish' },
  { key: 'certificate_inst', label: 'Certificate Institute' },
  { key: 'certificate_num', label: 'Certificate Number' },
  { key: 'certificate_link', label: 'Certificate Link' },
  { key: 'measurements', label: 'Measurements' },
  { key: 'lw_ratio', label: 'Ratio' },
  { key: 'table_width', label: 'Table Width' },
  { key: 'total_depth', label: 'Total Depth' },
  { key: 'sku', label: 'SKU' },
  { key: 'price', label: 'Price' },
  { key: 'image_link', label: 'Image Link' },
  { key: 'video_link', label: 'Video Link' }
];

const ExcelImportModal = ({ show, handleClose, onImport }) => {
  const [file, setFile] = useState(null);
  const [excelHeaders, setExcelHeaders] = useState([]);
  const [excelData, setExcelData] = useState([]);
  const [mapping, setMapping] = useState({});
  const [preview, setPreview] = useState([]);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [isValidated, setIsValidated] = useState(false);

  useEffect(() => {
    if (!show) {
      setFile(null);
      setExcelHeaders([]);
      setExcelData([]);
      setMapping({});
      setPreview([]);
      setError('');
      setValidationErrors([]);
      setIsValidated(false);
    }
  }, [show]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1 });
        
        if (data.length > 0) {
          const headers = data[0].filter(h => h && h.trim() !== "");
          setExcelHeaders(headers);
          
          const rows = XLSX.utils.sheet_to_json(ws);
          setExcelData(rows);

          // Auto-mapping logic
          const initialMapping = {};
          DIAMOND_FIELDS.forEach(field => {
            const match = headers.find(h => 
              h.toLowerCase().includes(field.key.toLowerCase()) || 
              h.toLowerCase().includes(field.label.toLowerCase())
            );
            if (match) initialMapping[field.key] = match;
          });
          setMapping(initialMapping);
        }
      } catch (err) {
        setError('Failed to parse Excel file.');
        console.error(err);
      }
    };
    reader.readAsBinaryString(selectedFile);
  };

  const handleMappingChange = (fieldKey, excelHeader) => {
    setMapping(prev => ({
      ...prev,
      [fieldKey]: excelHeader
    }));
    setIsValidated(false);
    setValidationErrors([]);
  };

  const handleValidation = () => {
    setError('');
    const numericFields = ['carat', 'price', 'weight', 'table_width', 'total_depth'];
    const errors = [];
    const validatedData = [];

    if (Object.keys(mapping).length === 0) {
      setError('Please map at least one column before validating.');
      return;
    }

    excelData.forEach((row, index) => {
      const diamond = {};
      const rowWarnings = [];

      DIAMOND_FIELDS.forEach(field => {
        if (mapping[field.key]) {
          let value = row[mapping[field.key]];
          
          // Basic Cleanup
          if (typeof value === 'string') value = value.trim();

          // Soft Numeric Conversion
          if (numericFields.includes(field.key) && value !== undefined && value !== null && value !== "") {
            const num = parseFloat(String(value).replace(/[^0-9.]/g, ''));
            if (isNaN(num)) {
              rowWarnings.push(`${field.label}: "${value}" is not a valid number`);
              diamond[field.key] = value; // Keep original if it fails conversion
            } else {
              diamond[field.key] = num;
            }
          } else if (field.key === 'source') {
            const sourceVal = value ? String(value).toLowerCase() : '';
            if (sourceVal !== 'lab grown' && sourceVal !== 'natural') {
              rowWarnings.push(`Source must be 'lab grown' or 'natural', got "${value || ''}"`);
            }
            diamond[field.key] = value;
          } else {
            diamond[field.key] = field.key === 'cut' && value ? String(value).toUpperCase() : value;
          }
        }
      });

      if (rowWarnings.length > 0) {
        errors.push({ row: index + 2, msg: rowWarnings.join(', ') });
      } else if (Object.keys(diamond).length > 0) {
        validatedData.push(diamond);
      }
    });

    setValidationErrors(errors);
    setPreview(validatedData);
    setIsValidated(true);

    if (errors.length > 0) {
      setError(`Notice: Found ${errors.length} rows with potential formatting issues. You can still proceed, but please verify your mapping.`);
    }
  };

  const handleProcessImport = () => {
    if (preview.length === 0) {
      setError('No valid rows to import. Please check your mapping and file data.');
      return;
    }
    onImport(preview);
  };

  const resetFile = () => {
    setFile(null);
    setExcelHeaders([]);
    setExcelData([]);
    setMapping({});
    setIsValidated(false);
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton className="bg-light">
        <Modal.Title className="fw-bold sunflower d-flex align-items-center gap-2">
          <UploadCloud size={24} className="text-primary" />
          Import Diamonds from Excel
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant={validationErrors.length > 0 ? "warning" : "danger"} className="d-flex align-items-center gap-2">
          <AlertCircle size={20} />
          {error}
        </Alert>}
        
        {!excelHeaders.length ? (
          <div className="p-5 text-center border-dashed rounded-4 bg-light">
            <Form.Group controlId="formFile" className="mb-3">
              <Form.Label className="fw-bold mb-4 h5">Step 1: Upload your Excel File</Form.Label>
              <div className="d-flex justify-content-center">
                <Form.Control 
                  type="file" 
                  accept=".xlsx, .xls" 
                  onChange={handleFileChange} 
                  className="w-75"
                />
              </div>
            </Form.Group>
            <p className="text-muted mt-4">Make sure the first row contains your column headers.</p>
          </div>
        ) : !isValidated ? (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="fw-bold sunflower mb-0 d-flex align-items-center gap-2">
                <FileSliders size={18} className="text-primary" />
                Step 2: Map Excel Columns
              </h6>
              <div className="d-flex gap-2">
                <Button variant="outline-secondary" size="sm" onClick={() => setMapping({})} className="text-decoration-none">Clear All Mappings</Button>
                <Button variant="link" size="sm" onClick={resetFile} className="text-decoration-none">Change File</Button>
              </div>
            </div>
            <div className="table-responsive border rounded-3" style={{ maxHeight: '400px' }}>
              <Table striped hover size="sm" className="mb-0">
                <thead className="sticky-top bg-white border-bottom shadow-sm">
                  <tr>
                    <th className="py-2 ps-3">JD Gems Field</th>
                    <th className="py-2">Your Excel Column</th>
                  </tr>
                </thead>
                <tbody>
                  {DIAMOND_FIELDS.map(field => (
                    <tr key={field.key}>
                      <td className="align-middle fw-medium ps-3 py-2">{field.label} {['carat', 'price'].includes(field.key) && <span className="text-danger">*</span>}</td>
                      <td className="py-2 pe-3">
                        <Form.Select 
                          size="sm" 
                          value={mapping[field.key] || ''} 
                          onChange={(e) => handleMappingChange(field.key, e.target.value)}
                          className={mapping[field.key] ? "border-primary bg-light" : ""}
                        >
                          <option value="">-- Skip this field --</option>
                          {excelHeaders.map(header => (
                            <option key={header} value={header}>{header}</option>
                          ))}
                        </Form.Select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        ) : (
          <div>
            <h6 className="fw-bold sunflower mb-3 d-flex align-items-center gap-2">
              <CheckCircle2 size={18} className="text-success" />
              Step 3: Validation Results
            </h6>
            
            <Row className="mb-4 g-3">
              <Col sm={4}>
                <div className="p-3 border rounded-3 bg-light text-center">
                  <div className="small text-muted mb-1">Total Rows</div>
                  <div className="h4 fw-bold mb-0">{excelData.length}</div>
                </div>
              </Col>
              <Col sm={4}>
                <div className="p-3 border rounded-3 bg-success bg-opacity-10 text-center border-success border-opacity-25">
                  <div className="small text-success mb-1">Valid Rows</div>
                  <div className="h4 fw-bold text-success mb-0">{preview.length}</div>
                </div>
              </Col>
              <Col sm={4}>
                <div className="p-3 border rounded-3 bg-danger bg-opacity-10 text-center border-danger border-opacity-25">
                  <div className="small text-danger mb-1">Error Rows</div>
                  <div className="h4 fw-bold text-danger mb-0">{validationErrors.length}</div>
                </div>
              </Col>
            </Row>

            {validationErrors.length > 0 && (
              <div className="mb-0">
                <p className="fw-bold small text-danger mb-2">Detailed Error Log:</p>
                <div className="border rounded-3 overflow-auto" style={{ maxHeight: '250px' }}>
                  <Table size="sm" striped hover className="mb-0 small">
                    <thead className="bg-light sticky-top">
                      <tr>
                        <th className="ps-3 py-2">Row</th>
                        <th className="py-2">Issue Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {validationErrors.map((err, i) => (
                        <tr key={i}>
                          <td className="ps-3 py-2 fw-bold text-muted">#{err.row}</td>
                          <td className="py-2 text-danger">{err.msg}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>
            )}
            
            <div className="mt-3 text-center">
              <Button variant="link" size="sm" onClick={() => setIsValidated(false)} className="text-decoration-none">
                Back to Mapping
              </Button>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="bg-light border-top-0">
        <Button variant="secondary" onClick={handleClose}>Cancel</Button>
        {excelHeaders.length > 0 && !isValidated && (
          <Button 
            variant="primary" 
            onClick={handleValidation}
            style={{ backgroundColor: 'var(--color-accent)', borderColor: 'var(--color-accent)' }}
          >
            Validate Data
          </Button>
        )}
        {isValidated && (
          <Button 
            variant="primary" 
            onClick={handleProcessImport}
            disabled={preview.length === 0}
            style={{ backgroundColor: 'var(--color-accent)', borderColor: 'var(--color-accent)' }}
          >
            Import {preview.length} Valid Rows
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ExcelImportModal;
